import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sliders, Save, BarChart3, Activity, Clock, Target, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useOcrSettings, useUpdateOcrSettings, useOcrStats } from '@/hooks/useOcr';
import { useAuth } from '@/lib/auth';

export default function OcrSettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: settings, isLoading } = useOcrSettings();
  const update = useUpdateOcrSettings();
  const { data: stats } = useOcrStats(30);

  const [draft, setDraft] = useState({
    confidence_threshold: '0.85',
    m3_min: '5',
    m3_max: '35',
    date_tolerance_days: 7,
    auto_apply_enabled: true,
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setDraft({
      confidence_threshold: settings.confidence_threshold,
      m3_min: settings.m3_min,
      m3_max: settings.m3_max,
      date_tolerance_days: settings.date_tolerance_days,
      auto_apply_enabled: settings.auto_apply_enabled,
    });
    setDirty(false);
  }, [settings]);

  const isAdmin = user?.role === 'super_admin';

  const onSave = async () => {
    await update.mutateAsync({
      confidence_threshold: Number(draft.confidence_threshold),
      m3_min: Number(draft.m3_min),
      m3_max: Number(draft.m3_max),
      date_tolerance_days: Number(draft.date_tolerance_days),
      auto_apply_enabled: draft.auto_apply_enabled,
    } as any);
    setDirty(false);
  };

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex items-center gap-3">
        <div className="rounded-lg bg-blue-50 p-2 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
          <Sliders className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-medium tracking-tight">{t('ocr.settings.title')}</h1>
          <p className="text-sm text-neutral-500">{t('ocr.settings.subtitle')}</p>
        </div>
      </header>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            icon={<BarChart3 className="h-4 w-4" />}
            label={t('ocr.stats.total')}
            value={String(stats.total)}
            sub={t('ocr.stats.window', { days: stats.window_days })}
          />
          <StatCard
            icon={<Target className="h-4 w-4 text-green-600" />}
            label={t('ocr.stats.extracted')}
            value={`${stats.extracted}`}
            sub={ratePct(stats.extracted, stats.total) + '% ' + t('ocr.stats.auto')}
          />
          <StatCard
            icon={<Activity className="h-4 w-4 text-amber-600" />}
            label={t('ocr.stats.review_needed')}
            value={`${stats.review_needed}`}
            sub={ratePct(stats.review_needed, stats.total) + '% ' + t('ocr.stats.review')}
          />
          <StatCard
            icon={<Zap className="h-4 w-4 text-blue-600" />}
            label={t('ocr.stats.avg_duration')}
            value={`${stats.avg_duration_ms}ms`}
            sub={t('ocr.stats.queue_depth') + ': ' + stats.queue_depth}
          />
        </div>
      )}

      {/* Settings */}
      {isLoading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : (
        <div className="rounded-lg border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-medium">{t('ocr.settings.thresholds')}</h2>
            {!isAdmin && (
              <span className="text-xs text-neutral-500">{t('ocr.settings.read_only')}</span>
            )}
          </div>

          <div className="space-y-5">
            <SettingRow
              label={t('ocr.settings.confidence_threshold')}
              hint={t('ocr.settings.confidence_threshold_hint')}
            >
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0.5"
                  max="0.99"
                  step="0.01"
                  value={draft.confidence_threshold}
                  onChange={(e) => {
                    setDraft({ ...draft, confidence_threshold: e.target.value });
                    setDirty(true);
                  }}
                  disabled={!isAdmin}
                  className="flex-1"
                />
                <div className="w-16 text-end font-mono text-sm">
                  {Math.round(Number(draft.confidence_threshold) * 100)}%
                </div>
              </div>
            </SettingRow>

            <SettingRow
              label={t('ocr.settings.m3_range')}
              hint={t('ocr.settings.m3_range_hint')}
            >
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  value={draft.m3_min}
                  onChange={(e) => { setDraft({ ...draft, m3_min: e.target.value }); setDirty(true); }}
                  disabled={!isAdmin}
                  className="w-24"
                />
                <span className="text-sm text-neutral-500">–</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={draft.m3_max}
                  onChange={(e) => { setDraft({ ...draft, m3_max: e.target.value }); setDirty(true); }}
                  disabled={!isAdmin}
                  className="w-24"
                />
                <span className="text-sm text-neutral-500">m³</span>
              </div>
            </SettingRow>

            <SettingRow
              label={t('ocr.settings.date_tolerance')}
              hint={t('ocr.settings.date_tolerance_hint')}
            >
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={draft.date_tolerance_days}
                  onChange={(e) => {
                    setDraft({ ...draft, date_tolerance_days: parseInt(e.target.value || '0') });
                    setDirty(true);
                  }}
                  disabled={!isAdmin}
                  className="w-24"
                />
                <span className="text-sm text-neutral-500">{t('ocr.settings.days')}</span>
              </div>
            </SettingRow>

            <SettingRow
              label={t('ocr.settings.auto_apply')}
              hint={t('ocr.settings.auto_apply_hint')}
            >
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.auto_apply_enabled}
                  onChange={(e) => { setDraft({ ...draft, auto_apply_enabled: e.target.checked }); setDirty(true); }}
                  disabled={!isAdmin}
                  className="h-4 w-4"
                />
                {t('ocr.settings.auto_apply_label')}
              </label>
            </SettingRow>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={onSave}
              disabled={!isAdmin || !dirty || update.isPending}
            >
              <Save className="mr-1 h-4 w-4" />
              {t('common.save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingRow({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
      <div className="sm:col-span-1">
        <label className="text-sm font-medium">{label}</label>
        {hint && <div className="mt-1 text-xs text-neutral-500">{hint}</div>}
      </div>
      <div className="sm:col-span-2">{children}</div>
    </div>
  );
}

function StatCard({
  icon, label, value, sub,
}: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center gap-2 text-xs text-neutral-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-medium">{value}</div>
      <div className="text-xs text-neutral-500">{sub}</div>
    </div>
  );
}

function ratePct(n: number, total: number): string {
  if (!total) return '0';
  return Math.round((n / total) * 100).toString();
}
